import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { notifications as notificationsApi, tokens } from '../utils/api';
import { useAuth } from './AuthContext';

/*
 * WebSocket بومی — نه socket.io. بک‌اند Django Channels یک WebSocket استاندارد
 * سرو می‌کند و پروتکل socket.io با آن سازگار نیست.
 */

const SocketContext = createContext(null);

function websocketUrl(token) {
  if (process.env.REACT_APP_WS_URL) {
    return `${process.env.REACT_APP_WS_URL}?token=${encodeURIComponent(token)}`;
  }
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${scheme}://${window.location.host}/ws/notifications/?token=${encodeURIComponent(token)}`;
}

const MAX_BACKOFF = 30000;

export function SocketProvider({ children }) {
  const { user } = useAuth();
  const [connected, setConnected] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  /* هر تغییر پرونده این شمارنده را جلو می‌برد؛ داشبوردها به آن گوش می‌دهند. */
  const [caseVersion, setCaseVersion] = useState(0);

  const socketRef = useRef(null);
  const retryRef = useRef(0);
  const timerRef = useRef(null);

  const loadFromServer = useCallback(() => {
    notificationsApi
      .list({ unread: 'true', page_size: 20 })
      .then(({ data }) => {
        setItems(data.results ?? []);
        setUnread(data.count ?? 0);
      })
      .catch(() => {
        /* شکست خواندن اعلان‌ها نباید کار اصلی کاربر را مختل کند. */
      });
  }, []);

  useEffect(() => {
    if (!user) {
      setItems([]);
      setUnread(0);
      return undefined;
    }

    /*
     * پرچم لغو مخصوصِ همین اجرای افکت است، نه یک ref مشترک. اگر ref مشترک باشد،
     * mount دوباره‌ی StrictMode آن را ریست می‌کند و onclose سوکت قبلی یک اتصال
     * دوم می‌سازد — نتیجه‌اش دو سوکت و اعلان تکراری بود.
     */
    let cancelled = false;
    loadFromServer();

    const connect = () => {
      if (cancelled) return;
      const token = tokens.access;
      if (!token) return;

      const socket = new WebSocket(websocketUrl(token));
      socketRef.current = socket;

      socket.onopen = () => {
        if (cancelled) {
          socket.close();
          return;
        }
        retryRef.current = 0;
        setConnected(true);
      };

      socket.onmessage = (event) => {
        if (cancelled) return;
        let payload;
        try {
          payload = JSON.parse(event.data);
        } catch {
          return;
        }

        if (payload.type === 'notification') {
          setItems((prev) => [payload, ...prev].slice(0, 30));
          setUnread((prev) => prev + 1);
          toast(payload.message, {
            type: payload.level === 'error' ? 'error' : payload.level || 'info',
            icon: payload.icon || '📌',
            autoClose: 6000,
          });
          setCaseVersion((v) => v + 1);
        } else if (payload.type === 'case_changed') {
          setCaseVersion((v) => v + 1);
        } else if (payload.type === 'ready') {
          setUnread(payload.unread ?? 0);
        }
      };

      socket.onclose = (event) => {
        if (cancelled) return;
        setConnected(false);
        socketRef.current = null;

        // 4401 یعنی توکن پذیرفته نشد؛ تلاش دوباره بی‌فایده است.
        if (event.code === 4401) return;

        retryRef.current += 1;
        const delay = Math.min(1000 * 2 ** (retryRef.current - 1), MAX_BACKOFF);
        timerRef.current = setTimeout(connect, delay);
      };

      socket.onerror = () => socket.close();
    };

    connect();

    return () => {
      cancelled = true;
      setConnected(false);
      clearTimeout(timerRef.current);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [user, loadFromServer]);

  const markRead = useCallback(async (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
    setUnread((prev) => Math.max(0, prev - 1));
    if (id) {
      try {
        await notificationsApi.markRead(id);
      } catch {
        loadFromServer();
      }
    }
  }, [loadFromServer]);

  const markAllRead = useCallback(async () => {
    setItems([]);
    setUnread(0);
    try {
      await notificationsApi.markAllRead();
    } catch {
      loadFromServer();
    }
  }, [loadFromServer]);

  const value = useMemo(
    () => ({ connected, notifications: items, unread, caseVersion, markRead, markAllRead }),
    [connected, items, unread, caseVersion, markRead, markAllRead]
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) throw new Error('useSocket باید داخل SocketProvider استفاده شود.');
  return context;
}
